import type { GameId } from '@gmp/shared-types/browser';
import { party } from '../net/client';
import { awayAtStart, onReturn, staleAfterAway } from '../net/activity';
import { gameInfo } from './catalog';
import { navigate, parse } from './router';

/**
 * Away from the app for an hour or more (opening it again, or coming back to a tab left in the background):
 * you land on Home, and the live parties you were in are left, so they drop off "Your parties". Turn-based
 * (async) games run over days, so their seats are kept: you're just taken off the screen. TV screens are
 * never touched.
 */
const isAsync = (gameId: GameId) => !!gameInfo(gameId)?.asyncInfo;

/** Once online, fetches your parties and leaves every live one. */
function leaveStaleLiveParties() {
  let asked = false;
  let before: unknown = undefined;
  const step = () => {
    const st = party.getState();
    if (st.status !== 'online') return;
    if (!asked) {
      asked = true;
      before = st.parties;
      party.myParties();
      return;
    }
    if (st.parties === before || !st.parties) return;
    unsubscribe();
    // Not the party you're in or joining right now (a join link opened on a device that had been away).
    const keep = new Set([st.room?.code, st.pendingCode, joinCode()].filter(Boolean));
    for (const p of st.parties) if (!isAsync(p.gameId) && !keep.has(p.code)) party.leaveParty(p.code);
  };
  const unsubscribe = party.subscribe(step);
  step();
}

function joinCode(): string | null {
  const r = parse();
  return r.name === 'join' ? r.code ?? null : null;
}

const AWAY_ROUTES = new Set(['party', 'parties']);

export function installAwayReset() {
  if (typeof window === 'undefined') return;
  if (staleAfterAway(awayAtStart) && !party.isDisplay && parse().name !== 'display') {
    if (AWAY_ROUTES.has(parse().name)) navigate({ name: 'home' }, true);
    leaveStaleLiveParties();
  }
  onReturn((away) => {
    if (!staleAfterAway(away) || party.isDisplay || parse().name === 'display') return;
    const { room } = party.getState();
    if (room && room.you.role !== 'display') {
      if (isAsync(room.selectedGame)) party.stepOut();
      else party.leave();
    }
    if (room || AWAY_ROUTES.has(parse().name)) navigate({ name: 'home' }, true);
    leaveStaleLiveParties();
  });
}
